import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@components/Button';
import { ModalConfirm } from '@components/Modal';
import PageHeader from '@features/PageHeader';
import { accountActions, alertActions, brandsActions } from '@actions';
import { brandsService } from '@services';
import { useTranslation } from '@hooks';
import { handleApiError } from '@helpers/api';
import { FORM_TYPE } from '@constants/brands';
import { BrandsIcon } from 'assets/images/icons';
import BrandsTable from './BrandsTable';
import EditModal from './EditModal';

const Brands = () => {
    const [selectedBrand, setSelectedBrand] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [modal, setModal] = useState({ open: false, type: FORM_TYPE.NEW });

    const locations = useSelector(state => state.account?.account?.companies || []);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(brandsActions.get(page, rowsPerPage));
    }, [page, rowsPerPage, dispatch]);

    useEffect(() => {
        // INFO: get all brands on unmount for using in other components
        return () => {
            dispatch(brandsActions.getAll());
        };
    }, []);

    const onDelete = async () => {
        try {
            await brandsService.deleteBrand(selectedBrand?.id);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            dispatch(accountActions.get());
            if (page === 0) {
                await dispatch(brandsActions.get(page, rowsPerPage));
            } else {
                setPage(0);
            }
            setDeleteModalOpen(false);
            setSelectedBrand({});
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail', { amount: error.count });
        }
    };

    return (
        <div style={{ paddingBottom: '16px' }}>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<BrandsIcon />}
                title={t('SettingsBrands.title')}
                subtitle={t('SettingsBrands.subtitle')}
            >
                <Button variant="contained" onClick={() => setModal(prev => ({ ...prev, open: true }))}>
                    {t('buttons.createBrand')}
                </Button>
            </PageHeader>
            <BrandsTable
                locations={locations}
                setSelectedBrand={setSelectedBrand}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                setModal={setModal}
                setDeleteModalOpen={setDeleteModalOpen}
            />
            {modal.open ? (
                <EditModal
                    selectedBrand={selectedBrand}
                    setSelectedBrand={setSelectedBrand}
                    modal={modal}
                    setModal={setModal}
                    setPage={setPage}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    locations={locations}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                    onSecondaryAction={() => {
                        setDeleteModalOpen(false);
                        setSelectedBrand({});
                    }}
                />
            )}
        </div>
    );
};

export default Brands;
